import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  fetchEntityRelationships,
  ILegalStructureEdge,
  ILegalStructureGraphResponse,
} from '../../../../services/queries/MaggieLegalStructureQueries';
import { selectCoHolderTargets } from './coHolders';

/**
 * Fetches the holders of each vehicle a rooted chart draws (FD-4933).
 *
 * One read per vehicle, because `GET /entityRelationship` filters a parent or a child and
 * never either end — the same two-read idiom the registry's `useEntityRelationships` uses.
 * The cost is bounded by the vehicle tier rather than the whole chart (see
 * `selectCoHolderTargets`), and React Query caches per node and date, so panning, focusing
 * and switching metric pay nothing.
 *
 * **The query key is `['entityRelationships', 'in', …]` on purpose.** `useStructureWrites`
 * already invalidates that prefix on every relationship write, so a newly drawn holding
 * refreshes these reads for free. A key of its own would have gone stale after a create and
 * shown a chart missing the edge someone had just added — silently, since nothing fails.
 *
 * `asOf` is passed through: edges are time-versioned BE-side, and an unpinned read would
 * answer for today while the chart shows another date.
 */
/** Handed over while the reads are still in flight; one identity, so the graph holds still. */
const NO_EDGES: ILegalStructureEdge[] = [];

export function useCoHolders(
  response: ILegalStructureGraphResponse | null | undefined,
  asOf: string,
  enabled: boolean
): { edges: ILegalStructureEdge[]; isIncomplete: boolean; isLoading: boolean } {
  const targets = useMemo(
    () => (enabled && response ? selectCoHolderTargets(response) : []),
    [enabled, response]
  );

  return useQueries({
    queries: targets.map((ref) => ({
      queryKey: ['entityRelationships', 'in', ref.nodeType, ref.nodeId, asOf],
      queryFn: () => fetchEntityRelationships({ childType: ref.nodeType, childId: ref.nodeId, asOf }),
      retry: false,
    })),
    // `combine` rather than a `useMemo` over the results: `useQueries` hands back a fresh
    // array every render, so a memo keyed on it would recompute each time and hand the
    // merge a new `edges` identity — which would re-run the layout, and the layout is what
    // holds the cards still.
    combine: (results) => ({
      /*
       * Nothing until every read has settled. Each vehicle is its own read and they land
       * one after another; handing over whatever has answered so far changes the edge list
       * on every landing, and each change rebuilds the graph and re-runs ELK. On a family
       * root with twenty vehicles that is twenty re-layouts in the first seconds after
       * load, the canvas blanking for each while React Flow re-measures — which reads as
       * the chart flickering. Held back, the chart lays out twice: bare, then merged.
       *
       * A null is the endpoint not being deployed and a rejection is one vehicle's read
       * failing; neither is a reason to withhold the holders that did answer. The merge
       * dedupes, so two vehicles reporting one holder needs no handling here.
       */
      edges: results.some((result) => result.isPending)
        ? NO_EDGES
        : results.flatMap((result) => result.data ?? []),
      /*
       * A read errored, so the co-holders on the chart are some of them rather than all.
       *
       * Reported rather than swallowed because the failure looks exactly like success:
       * `fetchEntityRelationships` rethrows anything that is not a 404/501, `retry: false`
       * means no second attempt, and the chart would then render looking complete while
       * omitting the very vehicle this exists to draw — the module's own bug, reproduced as
       * a failure mode. The surface says so instead.
       */
      isIncomplete: results.some((result) => result.isError),
      /*
       * A read has no answer yet, so the chart is not ready to draw.
       *
       * The surface holds its spinner on this. Merging the holders late instead painted the
       * root's own closure first and the finished chart a second later — a whole row of
       * holders appearing and everything re-laying out and refitting under the reader, on
       * every root switch. Pending rather than fetching: a refetch with an answer already
       * in hand keeps the chart on screen, so a write that invalidates these reads does not
       * blank it.
       */
      isLoading: results.some((result) => result.isPending),
    }),
  });
}
